import { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import Select from "react-select";
import { createTicket, getAllTicketLoggedTypes } from "../Api/TicketApiOrg";

const TicketCreateModal = ({
  show,
  onClose,
  orgId,
  userDetails,
  projectOptions,
  assigneeOptionsByProject,
  refreshTickets,
}) => {
  const [loading, setLoading] = useState(false);

  const [ticketReasons, setTicketReasons] = useState([]);
  const [ticketAssignees, setTicketAssignees] = useState([]);

  const ticketReasonOptions = ticketReasons.map((t) => ({
    value: t.code,
    label: t.descriptions,
  }));

  const [form, setForm] = useState({
    ticketName: "",
    description: "",
    priority: "Normal",
    category: "",
    ticketType: "Internal",
    projectAssigned: "",
    assignedTo: [],
    ticketReason: "",
    submittedBy: "",
  });

  const handleCreateTicket = async () => {
    try {
      if (
        !form.ticketName ||
        !form.description ||
        !form.category ||
        !form.ticketReason ||
        form.assignedTo.length === 0
      ) {
        toast.error("Please fill all required fields");
        return;
      }

      setLoading(true);

      const formData = new FormData();

      formData.append("OrgId", orgId);
      formData.append("TicketName", form.ticketName);
      formData.append("Description", form.description);
      formData.append("Priority", form.priority);
      formData.append("Category", form.category);
      formData.append("TicketType", form.ticketType);
      formData.append("TicketReason", form.ticketReason);
      formData.append("AssignedTo", form.assignedTo.join(","));
      formData.append("ProjectAssigned", form.projectAssigned);
      formData.append("CreatedBy", userDetails.userId);
      formData.append("FromMail", userDetails.email);
      formData.append("SubmittedBy", form.submittedBy);

      await createTicket(formData);

      toast.success("Ticket created successfully");

      refreshTickets();
      onClose();

      setForm({
        ticketName: "",
        description: "",
        priority: "Normal",
        category: "",
        ticketType: "Internal",
        projectAssigned: "",
        assignedTo: [],
        ticketReason: "",
        submittedBy: "",
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to create ticket");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!form.projectAssigned) {
      setTicketAssignees([]);
      return;
    }

    const projectIds = form.projectAssigned.split(",").map((id) => id.trim());

    const map = new Map();

    projectIds.forEach((pid) => {
      const assignees = assigneeOptionsByProject?.[pid] || [];

      assignees.forEach((opt) => {
        map.set(opt.value, opt);
      });
    });

    setTicketAssignees(Array.from(map.values()));
  }, [form.projectAssigned, assigneeOptionsByProject]);

  useEffect(() => {
    loadTicketReasons();
  }, []);

  const loadTicketReasons = async () => {
    try {
      const res = await getAllTicketLoggedTypes();
      setTicketReasons(res || []);
    } catch {
      toast.error("Failed to load ticket reasons");
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <span style={{ fontSize: "1.5rem", fontWeight: 700 }}>
            Create Ticket
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="resource-master-fields">
        <Form className="fields-form">
          {/* Row 1 */}
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Ticket Name</Form.Label>
                <Form.Control
                  value={form.ticketName}
                  onChange={(e) =>
                    setForm({ ...form, ticketName: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={form.priority}
                  onChange={(e) =>
                    setForm({ ...form, priority: e.target.value })
                  }
                >
                  <option value="Low">Low</option>
                  <option value="Normal">Normal</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Control
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Row 2 */}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Ticket Reason</Form.Label>

                <Select
                  options={ticketReasonOptions}
                  value={ticketReasonOptions.find(
                    (opt) => opt.value === form.ticketReason,
                  )}
                  onChange={(opt) =>
                    setForm({
                      ...form,
                      ticketReason: opt?.value || "",
                    })
                  }
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "50px",
                      height: "50px",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      height: "50px",
                      padding: "0 8px",
                    }),
                    input: (base) => ({
                      ...base,
                      margin: "0px",
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: "50px",
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Ticket Type</Form.Label>
                <Form.Select
                  value={form.ticketType}
                  onChange={(e) =>
                    setForm({ ...form, ticketType: e.target.value })
                  }
                >
                  <option value="Internal">Internal</option>
                  <option value="Client">Client</option>
                  <option value="Support">Support</option>
                  <option value="Technical">Technical</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Row 3 */}

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>

                <Form.Control
                  as="textarea"
                  rows={4}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Row 4 */}

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Project Assigned</Form.Label>

                <Select
                  isMulti
                  options={projectOptions}
                  value={projectOptions.filter((opt) =>
                    form.projectAssigned?.split(",").includes(opt.value),
                  )}
                  onChange={(selected) => {
                    const ids = selected ? selected.map((o) => o.value) : [];

                    setForm({
                      ...form,
                      projectAssigned: ids.join(","),
                      assignedTo: [],
                    });
                  }}
                  closeMenuOnSelect={false}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Row 5 */}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Assign To</Form.Label>

                <Select
                  isMulti
                  options={ticketAssignees}
                  value={ticketAssignees.filter((opt) =>
                    form.assignedTo.includes(opt.value),
                  )}
                  onChange={(selected) =>
                    setForm({
                      ...form,
                      assignedTo: selected ? selected.map((o) => o.value) : [],
                    })
                  }
                  closeMenuOnSelect={false}
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Submitted By</Form.Label>

                <Select
                  options={ticketAssignees}
                  value={ticketAssignees.find(
                    (opt) => opt.value === form.submittedBy,
                  )}
                  onChange={(selected) =>
                    setForm({
                      ...form,
                      submittedBy: selected?.value || "",
                    })
                  }
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onClose}
          style={{ padding: "11px 20px", fontSize: "13px" }}
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          onClick={handleCreateTicket}
          disabled={loading}
          style={{ padding: "10px 25px", fontSize: "15px" }}
        >
          {loading ? "Creating..." : "Create Ticket"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TicketCreateModal;

// import { useState, useEffect } from "react";
// import { Modal, Button, Form, Row, Col } from "react-bootstrap";
// import { toast } from "react-toastify";
// import Select from "react-select";
// import { createTicket, getAllTicketLoggedTypes } from "../Api/TicketApiOrg";

// const TicketCreateModal = ({
//   show,
//   onClose,
//   orgId,
//   userDetails,
//   projectOptions,
//   assigneeOptionsByProject,
//   refreshTickets,
// }) => {
//   const [loading, setLoading] = useState(false);

//   const [ticketReasons, setTicketReasons] = useState([]);

//   const [ticketAssignees, setTicketAssignees] = useState([]);

//   const ticketReasonOptions = ticketReasons.map((t) => ({
//     value: t.code,
//     label: t.descriptions,
//   }));

//   const [form, setForm] = useState({
//     ticketName: "",
//     description: "",
//     priority: "Normal",
//     category: "",
//     ticketType: "Internal",
//     projectAssigned: "",
//     assignedTo: [],
//     ticketReason: "",
//   });

//   const handleCreateTicket = async () => {
//     try {
//       if (
//         !form.ticketName ||
//         !form.description ||
//         !form.category ||
//         !form.ticketReason ||
//         form.assignedTo.length === 0
//       ) {
//         toast.error("Please fill all required fields");
//         return;
//       }

//       setLoading(true);

//       const payload = {
//         orgId: String(orgId),
//         ticketName: form.ticketName,
//         description: form.description,
//         priority: form.priority,
//         category: form.category,
//         ticketType: form.ticketType,
//         projectAssigned: form.projectAssigned,
//         AssignedTo: form.assignedTo.join(","),
//         TicketReason: form.ticketReason,
//         createdBy: String(userDetails.userId),
//         fromMail: userDetails.email,
//       };

//       const formData = new FormData();

//       formData.append("OrgId", orgId);
//       formData.append("TicketName", form.ticketName);
//       formData.append("Description", form.description);
//       formData.append("Priority", form.priority);
//       formData.append("Category", form.category);
//       formData.append("TicketType", form.ticketType);
//       formData.append("TicketReason", form.ticketReason);
//       formData.append("AssignedTo", form.assignedTo.join(","));
//       formData.append("ProjectAssigned", form.projectAssigned);
//       formData.append("CreatedBy", userDetails.userId);
//       formData.append("FromMail", userDetails.email);
//       formData.append("SubmittedBy", form.submittedBy);

//       await createTicket(formData);

//       toast.success("Ticket created successfully");

//       refreshTickets();
//       onClose();

//       setForm({
//         ticketName: "",
//         description: "",
//         priority: "Normal",
//         category: "",
//         ticketType: "Internal",
//         projectAssigned: "",
//         assignedTo: [],
//         ticketReason: "",
//         submittedBy: "",
//       });
//     } catch (error) {
//       console.error(error);
//       toast.error("Failed to create ticket");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (!form.projectAssigned) {
//       setTicketAssignees([]);
//       return;
//     }

//     const projectIds = form.projectAssigned.split(",").map((id) => id.trim());

//     const map = new Map();

//     projectIds.forEach((pid) => {
//       const assignees = assigneeOptionsByProject?.[pid] || [];

//       assignees.forEach((opt) => {
//         map.set(opt.value, opt);
//       });
//     });

//     setTicketAssignees(Array.from(map.values()));
//   }, [form.projectAssigned, assigneeOptionsByProject]);

//   useEffect(() => {
//     loadTicketReasons();
//   }, []);

//   const loadTicketReasons = async () => {
//     try {
//       const res = await getAllTicketLoggedTypes();
//       setTicketReasons(res || []);
//     } catch {
//       toast.error("Failed to load ticket reasons");
//     }
//   };

//   return (
//     <Modal show={show} onHide={onClose} size="lg">
//       <Modal.Header closeButton>
//         <Modal.Title>Create Ticket</Modal.Title>
//       </Modal.Header>

//       <Modal.Body>
//         <Form>
//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Ticket Name</Form.Label>
//                 <Form.Control
//                   value={form.ticketName}
//                   onChange={(e) =>
//                     setForm({ ...form, ticketName: e.target.value })
//                   }
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Priority</Form.Label>
//                 <Form.Select
//                   value={form.priority}
//                   onChange={(e) =>
//                     setForm({ ...form, priority: e.target.value })
//                   }
//                 >
//                   <option value="Low">Low</option>
//                   <option value="Normal">Normal</option>
//                   <option value="Medium">Medium</option>
//                   <option value="High">High</option>
//                   <option value="Critical">Critical</option>
//                 </Form.Select>
//               </Form.Group>
//             </Col>
//           </Row>

//           <Form.Group className="mb-3">
//             <Form.Label>Description</Form.Label>
//             <Form.Control
//               as="textarea"
//               rows={3}
//               value={form.description}
//               onChange={(e) =>
//                 setForm({ ...form, description: e.target.value })
//               }
//             />
//           </Form.Group>

//           <Form.Group className="mb-3">
//             <Form.Label>Ticket Reason</Form.Label>

//             <Select
//               options={ticketReasonOptions}
//               value={ticketReasonOptions.find(
//                 (opt) => opt.value === form.ticketReason,
//               )}
//               onChange={(opt) =>
//                 setForm({
//                   ...form,
//                   ticketReason: opt?.value || "",
//                 })
//               }
//               isClearable
//             />
//           </Form.Group>

//           <Form.Group className="mb-3">
//             <Form.Label>Assign To</Form.Label>

//             <Select
//               isMulti
//               options={ticketAssignees}
//               value={ticketAssignees.filter((opt) =>
//                 form.assignedTo.includes(opt.value),
//               )}
//               onChange={(selected) =>
//                 setForm({
//                   ...form,
//                   assignedTo: selected ? selected.map((o) => o.value) : [],
//                 })
//               }
//               closeMenuOnSelect={false}
//               isClearable
//             />
//           </Form.Group>

//           <Form.Group className="mb-3">
//             <Form.Label>Submitted By</Form.Label>

//             <Select
//               options={ticketAssignees}
//               value={ticketAssignees.find(
//                 (opt) => opt.value === form.submittedBy,
//               )}
//               onChange={(selected) =>
//                 setForm({
//                   ...form,
//                   submittedBy: selected?.value || "",
//                 })
//               }
//               isClearable
//             />
//           </Form.Group>

//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Category</Form.Label>
//                 <Form.Control
//                   value={form.category}
//                   onChange={(e) =>
//                     setForm({ ...form, category: e.target.value })
//                   }
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Ticket Type</Form.Label>
//                 <Form.Select
//                   value={form.ticketType}
//                   onChange={(e) =>
//                     setForm({ ...form, ticketType: e.target.value })
//                   }
//                 >
//                   <option value="Internal">Internal</option>
//                   <option value="Client">Client</option>
//                   <option value="Support">Support</option>
//                   <option value="Technical">Technical</option>
//                 </Form.Select>
//               </Form.Group>
//             </Col>
//           </Row>

//           <Form.Group className="mb-3">
//             <Form.Label>Project Assigned</Form.Label>

//             <Select
//               isMulti
//               options={projectOptions}
//               value={projectOptions.filter((opt) =>
//                 form.projectAssigned?.split(",").includes(opt.value),
//               )}
//               onChange={(selected) => {
//                 const ids = selected ? selected.map((o) => o.value) : [];

//                 setForm({
//                   ...form,
//                   projectAssigned: ids.join(","),
//                   assignedTo: [], // reset when project changes
//                 });
//               }}
//               closeMenuOnSelect={false}
//             />
//           </Form.Group>
//         </Form>
//       </Modal.Body>

//       <Modal.Footer>
//         <Button variant="secondary" onClick={onClose}>
//           Cancel
//         </Button>

//         <Button
//           variant="success"
//           onClick={handleCreateTicket}
//           disabled={loading}
//         >
//           {loading ? "Creating..." : "Create Ticket"}
//         </Button>
//       </Modal.Footer>
//     </Modal>
//   );
// };

// export default TicketCreateModal;
